/**
 * BundleProductPicker.js
 * Enhanced modal for selecting products for bundles with search and filters
 * Author: Markus.van.Kempen@gmail.com
 * Date: November 11, 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import AsyncProductImage from './AsyncProductImage';
import { queryMatchesSearchText, productRowSearchHaystack } from '../utils/siteSearch';
import './BundleProductPicker.css';

const BundleProductPicker = ({
  isOpen,
  onClose,
  selectedProductIds = [],
  onSelectionChange,
  excludeProductId = null
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [tempSelection, setTempSelection] = useState([...selectedProductIds]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/products', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out the current product being edited and bundle products
        const filtered = data.filter(p =>
          p.id !== excludeProductId &&
          !p.is_bundle &&
          p.is_bundle !== 1
        );
        setProducts(filtered);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [excludeProductId]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchCategories();
      setTempSelection([...selectedProductIds]);
    }
  }, [isOpen, selectedProductIds, fetchProducts, fetchCategories]);

  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm.trim() ||
      queryMatchesSearchText(productRowSearchHaystack(product), searchTerm, { title: product.name });

    const matchesCategory = selectedCategory === 'all' ||
      product.category === selectedCategory ||
      product.type === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Toggle product selection
  const handleToggleProduct = (productId) => {
    setTempSelection(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Select all filtered products
  const handleSelectAll = () => {
    const allFilteredIds = filteredProducts.map(p => p.id);
    const newSelection = [...new Set([...tempSelection, ...allFilteredIds])];
    setTempSelection(newSelection);
  };

  // Clear all selections
  const handleClearAll = () => {
    setTempSelection([]);
  };

  // Apply selection and close
  const handleApply = () => {
    onSelectionChange(tempSelection);
    onClose();
  };

  // Cancel and close
  const handleCancel = () => {
    setTempSelection([...selectedProductIds]);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = tempSelection.length;
  const selectedProducts = products.filter(p => tempSelection.includes(p.id));

  return (
    <div className="bundle-picker-overlay" onClick={handleCancel}>
      <div className="bundle-picker-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bundle-picker-header">
          <h2>📦 Select Products for Bundle</h2>
          <button className="bundle-picker-close" onClick={handleCancel}>
            ✕
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bundle-picker-filters">
          <div className="bundle-picker-search">
            <input
              type="text"
              placeholder="🔍 Search by name, author, artist, SKU, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bundle-picker-search-input"
            />
            {searchTerm && (
              <button
                className="bundle-picker-clear-search"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="bundle-picker-category-filter">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bundle-picker-category-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="bundle-picker-actions">
            <button
              onClick={handleSelectAll}
              className="bundle-picker-select-all"
              disabled={filteredProducts.length === 0}
            >
              Select All ({filteredProducts.length})
            </button>
            <button
              onClick={handleClearAll}
              className="bundle-picker-clear-all"
              disabled={selectedCount === 0}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Selection Summary */}
        <div className="bundle-picker-summary">
          <span className="selection-count">
            {selectedCount} product{selectedCount !== 1 ? 's' : ''} selected
          </span>
          {filteredProducts.length > 0 && (
            <span className="results-count">
              Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Selected Products Preview */}
        {selectedCount > 0 && (
          <div className="bundle-picker-selected-preview">
            <div className="selected-preview-header">
              <strong>Selected Products:</strong>
            </div>
            <div className="selected-preview-list">
              {selectedProducts.map(product => (
                <div key={product.id} className="selected-preview-item">
                  <span className="selected-preview-name">{product.name}</span>
                  <button
                    className="selected-preview-remove"
                    onClick={() => handleToggleProduct(product.id)}
                    title="Remove from selection"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="bundle-picker-content">
          {loading ? (
            <div className="bundle-picker-loading">
              <div className="spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bundle-picker-empty">
              <p>No products found</p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="bundle-picker-clear-filters"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="bundle-picker-grid">
              {filteredProducts.map(product => {
                const isSelected = tempSelection.includes(product.id);
                const isOutOfStock = !product.inventory || product.inventory === 0;
                return (
                  <div
                    key={product.id}
                    className={`bundle-picker-product ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                    onClick={() => handleToggleProduct(product.id)}
                  >
                    <div className="bundle-picker-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => { }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    <div className="bundle-picker-product-image">
                      {isOutOfStock && (
                        <div style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          background: 'var(--color-error, #dc3545)',
                          color: 'white',
                          padding: '3px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          zIndex: 2,
                          textTransform: 'uppercase'
                        }}>
                          Out of Stock
                        </div>
                      )}
                      <AsyncProductImage
                        product={product}
                        alt={product.name}
                        className="product-thumbnail"
                      />
                    </div>

                    <div className="bundle-picker-product-info">
                      <h4 className="product-name">{product.name}</h4>
                      {(product.author || product.artist) && (
                        <p className="product-author">
                          {product.author || product.artist}
                        </p>
                      )}
                      <div className="product-meta">
                        <span className="product-category">
                          {product.category || product.type}
                        </span>
                        <span className="product-price">
                          ${(product.price_cents / 100).toFixed(2)}
                        </span>
                      </div>
                      {product.sku && (
                        <p className="product-sku">SKU: {product.sku}</p>
                      )}
                      <div className="product-stock" style={{
                        fontSize: '12px',
                        marginTop: '5px',
                        color: isOutOfStock ? 'var(--color-error, #dc3545)' : 'var(--color-success, #28a745)',
                        fontWeight: '600'
                      }}>
                        {isOutOfStock ? '⚠️ Out of Stock' : `✓ ${product.inventory} in stock`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bundle-picker-footer">
          <button
            className="bundle-picker-cancel"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="bundle-picker-apply"
            onClick={handleApply}
            disabled={selectedCount === 0}
          >
            Apply Selection ({selectedCount} product{selectedCount !== 1 ? 's' : ''})
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleProductPicker;

